import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Download, Users, User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { glassCardStatic } from '@/utils/admin-glass-styles';

interface CancellationStat {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  total_bookings: number;
  cancellation_count: number;
  cancellation_rate: number;
}

type TimeFilter = 'all' | '30' | '90';

export const CancellationStats: React.FC = () => {
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const [clientStats, setClientStats] = useState<CancellationStat[]>([]);
  const [practitionerStats, setPractitionerStats] = useState<CancellationStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');

  useEffect(() => {
    fetchCancellationStats();
  }, [timeFilter]);

  const getDateFilter = () => {
    const now = new Date();
    switch (timeFilter) {
      case '30':
        now.setDate(now.getDate() - 30);
        return now.toISOString();
      case '90':
        now.setDate(now.getDate() - 90);
        return now.toISOString();
      default:
        return '1970-01-01';
    }
  };

  const fetchCancellationStats = async () => {
    setIsLoading(true);
    try {
      const dateFilter = getDateFilter();

      // Fetch client cancellation stats
      const { data: clientData, error: clientError } = await supabase
        .from('bookings')
        .select(`
          client_id,
          status,
          created_at,
          clients!inner(id, first_name, last_name)
        `)
        .gte('created_at', dateFilter);

      if (clientError) throw clientError;

      // Fetch practitioner cancellation stats
      const { data: practitionerData, error: practitionerError } = await supabase
        .from('bookings')
        .select(`
          practitioner_id,
          status,
          created_at,
          practitioner_declined_at,
          practitioners!inner(id, first_name, last_name)
        `)
        .gte('created_at', dateFilter);

      if (practitionerError) throw practitionerError;

      // Get emails from auth.users
      const { data: usersData } = await supabase.auth.admin.listUsers();

      // Process client stats
      const clientStatsMap = new Map<string, CancellationStat>();
      (clientData as any[])?.forEach((booking: any) => {
        const clientId = booking.client_id;
        if (!clientStatsMap.has(clientId)) {
          const user = usersData?.users.find((u: any) => u.id === clientId);
          clientStatsMap.set(clientId, {
            id: clientId,
            first_name: booking.clients?.first_name || null,
            last_name: booking.clients?.last_name || null,
            email: user?.email || 'N/A',
            total_bookings: 0,
            cancellation_count: 0,
            cancellation_rate: 0,
          });
        }
        const stat = clientStatsMap.get(clientId)!;
        stat.total_bookings++;
        if (booking.status === 'Cancelled') {
          stat.cancellation_count++;
        }
      });

      // Calculate cancellation rates for clients
      clientStatsMap.forEach((stat) => {
        stat.cancellation_rate = stat.total_bookings > 0
          ? Math.round((stat.cancellation_count / stat.total_bookings) * 100 * 100) / 100
          : 0;
      });

      // Process practitioner stats
      const practitionerStatsMap = new Map<string, CancellationStat>();
      (practitionerData as any[])?.forEach((booking: any) => {
        const practitionerId = booking.practitioner_id;
        if (!practitionerStatsMap.has(practitionerId)) {
          const user = usersData?.users.find((u: any) => u.id === practitionerId);
          practitionerStatsMap.set(practitionerId, {
            id: practitionerId,
            first_name: booking.practitioners?.first_name || null,
            last_name: booking.practitioners?.last_name || null,
            email: user?.email || 'N/A',
            total_bookings: 0,
            cancellation_count: 0,
            cancellation_rate: 0,
          });
        }
        const stat = practitionerStatsMap.get(practitionerId)!;
        stat.total_bookings++;
        // Count declined bookings as cancellations
        if (booking.status === 'Cancelled' && booking.practitioner_declined_at) {
          stat.cancellation_count++;
        }
      });

      // Calculate cancellation rates for practitioners
      practitionerStatsMap.forEach((stat) => {
        stat.cancellation_rate = stat.total_bookings > 0
          ? Math.round((stat.cancellation_count / stat.total_bookings) * 100 * 100) / 100
          : 0;
      });

      // Sort by cancellation count descending
      const sortedClientStats = Array.from(clientStatsMap.values())
        .filter(stat => stat.total_bookings > 0)
        .sort((a, b) => b.cancellation_count - a.cancellation_count);

      const sortedPractitionerStats = Array.from(practitionerStatsMap.values())
        .filter(stat => stat.total_bookings > 0)
        .sort((a, b) => b.cancellation_count - a.cancellation_count);

      setClientStats(sortedClientStats);
      setPractitionerStats(sortedPractitionerStats);
    } catch (error) {
      console.error('Error fetching cancellation stats:', error);
      toast({
        title: t('error'),
        description: 'Failed to fetch cancellation statistics',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = (data: CancellationStat[], filename: string) => {
    const headers = [
      t('cancellationStats.columns.name'),
      t('cancellationStats.columns.email'),
      t('cancellationStats.columns.totalBookings'),
      t('cancellationStats.columns.cancellations'),
      t('cancellationStats.columns.rate'),
    ];

    const rows = data.map(stat => [
      `${stat.first_name || ''} ${stat.last_name || ''}`.trim() || 'N/A',
      stat.email,
      stat.total_bookings,
      stat.cancellation_count,
      `${stat.cancellation_rate}%`,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const renderStatsTable = (data: CancellationStat[], type: 'client' | 'practitioner') => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Select value={timeFilter} onValueChange={(value) => setTimeFilter(value as TimeFilter)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cancellationStats.filters.allTime')}</SelectItem>
            <SelectItem value="30">{t('cancellationStats.filters.last30Days')}</SelectItem>
            <SelectItem value="90">{t('cancellationStats.filters.last90Days')}</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => exportToCSV(data, `${type}_cancellations`)}
        >
          <Download className="h-4 w-4 mr-2" />
          {t('cancellationStats.export')}
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('cancellationStats.columns.name')}</TableHead>
              <TableHead>{t('cancellationStats.columns.email')}</TableHead>
              <TableHead className="text-right">{t('cancellationStats.columns.totalBookings')}</TableHead>
              <TableHead className="text-right">{t('cancellationStats.columns.cancellations')}</TableHead>
              <TableHead className="text-right">{t('cancellationStats.columns.rate')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length > 0 ? (
              data.map((stat) => (
                <TableRow key={stat.id}>
                  <TableCell className="font-medium">
                    {`${stat.first_name || ''} ${stat.last_name || ''}`.trim() || 'N/A'}
                  </TableCell>
                  <TableCell>{stat.email}</TableCell>
                  <TableCell className="text-right">{stat.total_bookings}</TableCell>
                  <TableCell className="text-right font-semibold text-red-600">
                    {stat.cancellation_count}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={stat.cancellation_rate > 20 ? 'text-red-600 font-semibold' : ''}>
                      {stat.cancellation_rate}%
                    </span>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  {t('noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <Card className={glassCardStatic}>
      <CardHeader>
        <CardTitle>{t('cancellationStats.title')}</CardTitle>
        <CardDescription>
          View cancellation statistics by clients and practitioners to identify patterns
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="clients">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="clients" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t('cancellationStats.clientTab')}
            </TabsTrigger>
            <TabsTrigger value="practitioners" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {t('cancellationStats.practitionerTab')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clients">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              renderStatsTable(clientStats, 'client')
            )}
          </TabsContent>

          <TabsContent value="practitioners">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              renderStatsTable(practitionerStats, 'practitioner')
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
